import { Component, ElementRef, EventEmitter, Injector, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';

import { DndDropEvent } from 'ngx-drag-drop';
import { ToastMessageOptions, MessageService } from 'primeng/api';
import { Select } from 'primeng/select';
import { BehaviorSubject, Subscription } from 'rxjs';

import { mentionBadgesSaved } from '../../../../Common/Mentions/components/MentionCard/mention-card.component';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { DropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';
import { Mention } from '../../../../Common/Mentions/models/mention';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { BlockModel } from '../../../../Common/PageBuilder/models/block.model';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { BriefingSortByEnum } from '../../enums/briefing-sort-by.enum';
import { curatedMentionListBlockType } from '../../models/briefings-block-types.model';
import { CuratedMentionListDisplayElementModel } from '../../models/curated-mention-list-display-element.model';
import { BriefingsService } from '../../services/briefings.service';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';
import { MentionWorkbenchDialogComponent } from '../../../../Common/Mentions/components/MentionWorkbenchDialog/mention-workbench-dialog.component';
import { LayoutBlock } from '@Common/PageBuilder/models/layout-block.model';

@Component({
    selector: 'agpr-sort-tool',
    templateUrl: './sort-tool.component.html',
    styleUrls: ['./sort-tool.component.scss'],
    standalone: false
})
export class SortToolComponent implements OnInit, OnDestroy {
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly badges = mentionBadgesSaved;
    readonly buttonTypes = ButtonTypesEnum;
    readonly curatedBlockKey: string = curatedMentionListBlockType.key;
    // random key
    readonly untitledKey: string = '7bd1c835e9d1';

    private subscriptions: Subscription[] = [];

    public selectedBlock: BlockModel;
    public curatedDisplayElement: CuratedMentionListDisplayElementModel;

    public mentions: Mention[] = [];
    public selectedMentions: Mention[] = [];

    public curatedBlocks: BlockModel[] = [];
    public moveToOptions: DropdownOptionModel[] = [];
    public selectedMoveToBlock: BlockModel;
    public selectedMoveToOption: DropdownOptionModel = null;

    public showSelectAll: boolean = true;

    public id;

    @Output() draggingMentions: EventEmitter<boolean> = new EventEmitter();
    @Output() selectedMentionChange: EventEmitter<boolean> = new EventEmitter();
    @ViewChild('moveToDropdown') moveToDropdown: Select;
    @ViewChild('counter') counter: ElementRef;


    draggingItems: Mention[];
    // offset the move counter from the mouse position
    readonly dragMoveCounterOffset: any = {
        x: -12, // moves it right 12 pixels
        y: -12 // moves it down 12 pixels
    };

    constructor(
        public briefingsService: BriefingsService,
        public messageService: MessageService,
        public localizationService: LocalizationService,
        public coreDialogService: CoreDialogService,
        public injector: Injector
    ) {
    }

    ngOnInit() {
        this.subscriptions.push(
            this.briefingsService.getSelectedBlock().subscribe(
                (block) => {
                    this.selectedBlock = block;
                    this.refreshMentions();
                    this.updateMoveToOptions();
                }
            ),
            this.briefingsService.getBlocks().subscribe(
                (blocks) => {
                    this.curatedBlocks = [];
                    blocks.forEach(block => {
                        if (block.isLayoutBlockType()) {
                            (block as LayoutBlock).blocks.forEach(column => {
                                column.forEach(x => {
                                    if (x.type && x.type.key === curatedMentionListBlockType.key) {
                                        this.curatedBlocks.push(x);
                                    }
                                });
                            });
                        } else {
                            if (block.type && block.type.key === curatedMentionListBlockType.key) {
                                this.curatedBlocks.push(block);
                            }
                        }
                    });
                    this.refreshMentions();
                    this.updateMoveToOptions();
                }
            ),
            this.briefingsService.getCurrentBriefingMentions().subscribe(
                (mentions) => {
                    requestAnimationFrame(() => {
                        this.refreshMentions();
                    });
                }
            )
        );
    }

    /**
     * Updates the mentions blocks
     */
    private refreshMentions(): void {
        if (this.selectedBlock?.type?.key === curatedMentionListBlockType.key) {
            const curatedDisplayElement = this.selectedBlock.displayElement as CuratedMentionListDisplayElementModel;
            this.curatedDisplayElement = curatedDisplayElement;
            // const selectedMentions = this.mentions.filter(mention => mention.selected);
            this.mentions = curatedDisplayElement.mentions.map(apiMention => new Mention(apiMention));
            this.selectedMentions = this.selectedMentions
                .map(old => this.mentions.find(mention => {
                    if (mention.id && old.id) {
                        return mention.id === old.id;
                    } else {
                        return mention.mentionId === old.mentionId;
                    }
                }))
                .filter(mention => !!mention);
        } else {
            this.mentions = [];
            this.selectedMentions = [];
        }
    }


    selectMentions() {
        if (this.selectedMentions.length === this.mentions.length) {
            this.selectedMentions = [];
            this.selectedMentionChange.emit(false);
            this.showSelectAll = true;
        } else {
            this.selectedMentions = this.mentions.slice();
            this.selectedMentionChange.emit(true);
            this.showSelectAll = false;
        }
    }

    /**
     * Handles changing the "selected" state of a mention
     */
    selectedMentionsChange() {
        this.selectedMentionChange.emit(this.selectedMentions.length > 0);
    }

    /**
     * Used by the template to handle tracking on the mentions loop
     */
    public mentionTrackBy(index, mention) {
        return mention ? mention.id : null;
    }

    private getOrderedSelectedMentions(): Mention[] {
        return this.mentions.filter(mention => this.selectedMentions.includes(mention));
    }

    /**
     * Checks if a set of mentions are consecutive
     */
    private mentionsAreConsecutive(selectedMentions: Mention[]): boolean {
        const firstIndex = this.mentions.indexOf(selectedMentions[0]);
        const lastIndex = this.mentions.indexOf(selectedMentions[selectedMentions.length - 1]);
        if (lastIndex - firstIndex !== selectedMentions.length - 1) {
            return false;
        }
        return true;
    }

    /**
     * Updates the displayElement model's mentions based on the updated list
     */
    private updateDisplayModelMentions(): void {
        this.curatedDisplayElement.sortBy = BriefingSortByEnum.MANUAL;
        this.curatedDisplayElement.mentions = this.mentions.map((mention) => {
            return mention.convertToApiMention();
        });
        this.curatedDisplayElement.mentionIds = this.mentions.map((mention) => {
            if (mention.mentionId) {
                return mention.mentionId;
            } else {
                return mention.id;
            }
        });
        this.briefingsService.formFieldUpdated();
    }

    /**
     * Moves the selected mentions up
     * If there are multiple non-consecutive mentions, then group them next to the top-most one instead
     */
    moveUp(): void {
        const selectedMentions = this.getOrderedSelectedMentions();
        const mentions = this.mentions;
        const consecutive = this.mentionsAreConsecutive(selectedMentions);
        const firstIndex = mentions.indexOf(selectedMentions[0]);

        if (consecutive) {
            // mentions are consecutive, so move them all together
            if (firstIndex === 0) {
                // already at the top, so nothing to move
                return;
            }
            const movedMentions = mentions.splice(firstIndex, selectedMentions.length);
            mentions.splice(firstIndex - 1, 0, ...movedMentions);
        } else {
            // mentions are not consecutive, so move them all to the first selected one
            const newOrder = [];
            for (let i = 0, l = mentions.length; i < l; i++) {
                const mention = mentions[i];
                if (selectedMentions.indexOf(mention) === -1) {
                    newOrder.push(mention);
                }
            }
            newOrder.splice(firstIndex, 0, ...selectedMentions);
            this.mentions = newOrder;
        }
        this.updateDisplayModelMentions();
    }

    /**
     * Moves the selected mentions down
     * If there are multiple non-consecutive mentions, then group them next to the bottom-most one instead
     */
    moveDown(): void {
        const selectedMentions = this.getOrderedSelectedMentions();
        const mentions = this.mentions;
        const consecutive = this.mentionsAreConsecutive(selectedMentions);
        const lastIndex = mentions.indexOf(selectedMentions[selectedMentions.length - 1]);

        if (consecutive) {
            // mentions are consecutive, so move them all together
            if (lastIndex === mentions.length - 1) {
                // already at the top, so nothing to move
                return;
            }
            const firstIndex = mentions.indexOf(selectedMentions[0]);
            const movedMentions = mentions.splice(firstIndex, selectedMentions.length);
            mentions.splice(firstIndex + 1, 0, ...movedMentions);
        } else {
            // mentions are not consecutive, so move them all to the first selected one
            const newOrder = [];
            for (let i = 0, l = mentions.length; i < l; i++) {
                const mention = mentions[i];
                if (selectedMentions.indexOf(mention) === -1) {
                    newOrder.push(mention);
                }
            }
            newOrder.splice(lastIndex - selectedMentions.length + 1, 0, ...selectedMentions);
            this.mentions = newOrder;
        }
        this.updateDisplayModelMentions();
    }

    /**
     * Deletes the selected mentions from the mention list
     */
    delete(noFeedback?: boolean): void {
        const selectedMentions = this.selectedMentions;
        const selectedMentionsLength = selectedMentions.length;
        const mentions = this.mentions;

        const newOrder = [];
        for (let i = 0, l = mentions.length; i < l; i++) {
            const mention = mentions[i];
            if (!selectedMentions.includes(mention)) {
                newOrder.push(mention);
            }
        }
        this.mentions = newOrder;
        this.updateDisplayModelMentions();

        if (
            (this.selectedBlock.displayElement as CuratedMentionListDisplayElementModel).title === this.briefingsService.getMentionsOfInterestTitle()
            && this.mentions.length === 0
        ) {
            this.briefingsService.removeBlock(this.selectedBlock);
        }

        if (noFeedback) {
            return;
        }

        const successMessage: ToastMessageOptions = {
            severity: messageSeverityEnum.success,
            summary: this.localizationService.transform('Briefings.sortTool.mentionRemoved'),
            detail: this.localizationService.transform(
                'Briefings.sortTool.mentionRemovedSucessMessage',
                { 'number': selectedMentionsLength }
            )
        };
        this.messageService.add(successMessage);
    }

    updateMoveToOptions(): void {
        this.moveToOptions = this.curatedBlocks
            .filter((block) => {
                return block !== this.selectedBlock;
            })
            .map((block) => {
                const displayElement = block.displayElement as CuratedMentionListDisplayElementModel;

                if (displayElement.title) {
                    return new DropdownOptionModel(displayElement.title, block);
                }

                return new DropdownOptionModel(this.untitledKey, block);
            }).sort( (a, b) => {
                const nameOfA = a.label.toLowerCase();
                const nameOfB = b.label.toLowerCase();

                return nameOfA < nameOfB ? -1 : nameOfA > nameOfB ? 1 : 0;
            });
    }

    /**
     * Moves a set of mentions to another curated block
     */
    moveTo(otherBlock: BlockModel): void {
        if (!otherBlock) {
            return;
        }

        // de-select the destination
        this.selectedMoveToOption = new DropdownOptionModel();

        // copy mentions to the other block
        const selectedMentions = this.getOrderedSelectedMentions();
        const apiMentions = [];

        const otherDisplayElement = otherBlock.displayElement as CuratedMentionListDisplayElementModel;
        let numOfDuplicatedMentions = 0;
        selectedMentions.forEach(item => {
            if (otherDisplayElement.mentions?.some(otherBlockMention => otherBlockMention.id === item.mentionId)) {
                // destination block already contains this mention
                numOfDuplicatedMentions ++;
                this.selectedMentions = this.selectedMentions.filter(x => x.id !== item.id);
            } else {
                apiMentions.push(item.convertToApiMention());
            }
        });

        // This and the above if statement need to be separate in case the mentions haven't yet been loaded
        if (otherDisplayElement.mentions) {
            otherDisplayElement.mentions = [
                ...apiMentions,
                ...otherDisplayElement.mentions
            ];
        } else {
            otherDisplayElement.mentions = apiMentions;
        }

        otherDisplayElement.sortMentions();

        if (numOfDuplicatedMentions) {
            this.messageService.add({
                severity: messageSeverityEnum.error,
                summary: this.localizationService.transform('Briefings.sortTool.duplicatedMention.summary'),
                detail: this.localizationService.transform(
                    'Briefings.sortTool.duplicatedMention.details',
                    { 'number': numOfDuplicatedMentions }
                )
            });
        }

        // These things need to happen on the next frame or primeNG will throw a "change after check" error
        window.requestAnimationFrame( () => {
            // deleting will cause the dropdown to be disabled, which then causes it to lose focus
            // if we don't tell it that it's already unfocused it will also cause a "change after check" error
            this.moveToDropdown.focused = false;

            // after copying to the other block, they can be deleted from this one
            this.delete(true);
        });
    }

    /**
     * When the section title is updated, notify the page builder
     */
    updateSectionTitle(): void {
        this.briefingsService.formFieldUpdated();
    }

    /**
     * Handles the event when a mention is dropped into a new position in the mention list
     * If the mention was selected and there are more selected mentions, then moves all of them to the new position
     */
    onDropMentions(event: DndDropEvent): void {
        if (event.index === undefined || event.index === null) {
            return;
        }
        if (event.dropEffect === 'move') {
            let index = event.index;
            const mentions = this.mentions.slice();

            // remove all of the items from their original positions
            for (const mention of this.draggingItems) {
                const i = mentions.indexOf(mention);
                if (i < index) {
                    index--;
                }
                mentions.splice(i, 1);
            }
            // insert the items in the order they are selected from top to bottom
            mentions.splice(index, 0, ...this.draggingItems);
            this.mentions = mentions;
            this.updateDisplayModelMentions();
        }
    }

    /**
     * Start event handler for dragging a mention or series of mentions.
     * If the drag event is started on a mention that is selected, then the drag will be treated as it is being performed on all currently selected mentions.
     */
    onDragStartMentions(mention: Mention, event: DragEvent): void {
        this.draggingMentions.emit(true);
        let draggingOrder = [];

        if (this.selectedMentions.includes(mention)) {
            draggingOrder = this.getOrderedSelectedMentions();
        } else {
            draggingOrder = [mention];
        }

        this.draggingItems = draggingOrder;
        this.showDragCounter(event);
    }

    /**
     * Cleans up after a drag has been completed
     */
    public cleanupAfterDragCompleted(event: any): void {
        this.draggingMentions.emit(false);
        this.draggingItems = null;
        this.counter.nativeElement.innerHTML = '0';
        setTimeout(() => {
            if (event?.target?.style) {
                event.target.style.pointerEvents = 'unset';
            }
        }, 500);
    }

    /**
     * Creates a counter image to display instead of the single item when dragging multiple items
     */
    public showDragCounter(event: DragEvent): void {
        if (this.draggingItems && this.draggingItems.length > 1) {
            this.counter.nativeElement.innerHTML = '' + this.draggingItems.length;
            event.dataTransfer.setDragImage(
                this.counter.nativeElement,
                this.dragMoveCounterOffset.x,
                this.dragMoveCounterOffset.y
            );
        }
    }

    editMention(mention: Mention){
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        const ioModel =  {
            inputs: {
                animationTrigger: animationTrigger,
                mention
            },
            outputs: {
                savedMention: (_mention: Mention) => {
                    this.mentionUpdated(_mention);
                },
                insightsChanged: (_mention: Mention) => {
                    this.mentionUpdated(_mention);
                }
            }
        };

        this.coreDialogService.open(MentionWorkbenchDialogComponent, ioModel, this.injector);
    }

    private mentionUpdated(mention: Mention) {
        let mentionIndex = null;
        this.mentions.forEach((item, index) => {
            if (mention.id && mention.id === item.id) {
                mentionIndex = index;
            } else if (!mention.id && mention.mentionId === item.mentionId) {
                mentionIndex = index;
            }
        });

        if (mentionIndex !== null) {
            this.mentions.splice(mentionIndex, 1, mention);
            this.updateDisplayModelMentions();
        }
    }

    deleteMention(mention: Mention){
        const index = this.mentions.indexOf(mention);
        if (index > -1) {
            this.mentions.splice(index, 1);
        }
        this.updateDisplayModelMentions();
    }

    ngOnDestroy() {
        this.subscriptions.forEach((sub) => sub.unsubscribe());
    }
}
